import { AlertTriangle } from 'lucide-react';
import { formatEur } from './charts/IbcsTooltip';
import type { ByEntityData } from '../../types/cashCockpit';

interface Props {
    data: ByEntityData;
}

export default function ByEntityTab({ data }: Props) {
    // Build pivot: rows = companyCode, columns = yearMonth (newest on LEFT)
    const months = Array.from(new Set(data.matrix.map(r => r.yearMonth))).sort().reverse();
    const companies = Array.from(new Set(data.matrix.map(r => r.companyCode))).sort();
    const valueMap = new Map<string, number>();
    data.matrix.forEach(r => valueMap.set(`${r.companyCode}|${r.yearMonth}`, r.totalEUR));
    const staleMap = new Map(data.staleness.map(s => [s.companyCode, s]));

    // Max absolute value for heatmap intensity
    const maxAbs = Math.max(1, ...Array.from(valueMap.values()).map(v => Math.abs(v)));

    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <h3 className="text-sm font-semibold text-gray-900 px-4 py-3 border-b border-gray-200">
                Cash by Entity × Month (EUR)
            </h3>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-600">
                        <tr>
                            <th className="sticky left-0 bg-gray-50 px-3 py-2 text-left font-medium">Entity</th>
                            <th className="px-2 py-2 text-center font-medium">Status</th>
                            {months.map(m => (
                                <th key={m} className="px-2 py-2 text-right font-medium font-mono">{m}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {companies.map(c => {
                            const st = staleMap.get(c);
                            return (
                                <tr key={c} className="border-t border-gray-100">
                                    <td className="sticky left-0 bg-white px-3 py-1.5 font-semibold uppercase text-gray-800">{c}</td>
                                    <td className="px-2 py-1.5 text-center">
                                        {st && st.severity !== 'OK' ? (
                                            <span
                                                title={`${st.daysSinceLastEntry} days since last entry`}
                                                className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${
                                                    st.severity === 'Critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                                }`}
                                            >
                                                <AlertTriangle className="w-3 h-3" /> {st.daysSinceLastEntry}d
                                            </span>
                                        ) : (
                                            <span className="text-gray-400 text-[10px]">—</span>
                                        )}
                                    </td>
                                    {months.map(m => {
                                        const v = valueMap.get(`${c}|${m}`);
                                        const intensity = v != null ? Math.min(1, Math.abs(v) / maxAbs) : 0;
                                        const bg = v == null
                                            ? 'transparent'
                                            : `rgba(31, 41, 55, ${0.05 + intensity * 0.25})`;
                                        return (
                                            <td
                                                key={m}
                                                className="px-2 py-1.5 text-right font-mono tabular-nums"
                                                style={{ backgroundColor: bg }}
                                            >
                                                {v != null ? formatEur(v) : '—'}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="text-xs text-gray-500 px-4 py-2 bg-gray-50 border-t border-gray-200">
                Staleness badge appears when no entry in the last 14 days. Cell shading reflects absolute cash magnitude.
            </div>
        </div>
    );
}
